import type { ReactNode } from "react";
import { isInIframe, isUnifiedRoute } from "@/shared/utils";
import type { DocumentSource } from "../../../document";

type BuildDocumentFileLinkTooltipParams = {
  fileName: string;
  source: DocumentSource | null;
  extraLines?: string[];
  disableLinkAction?: boolean;
};

function getSourceLabel(source: DocumentSource | null): string | null {
  switch (source?.kind) {
    case "filestore":
      return "Файловое хранилище";
    case "s3":
      return "Облачное хранилище";
    case "presigned-url":
      return "Внешняя ссылка";
    case "local":
      return "Локальный файл";
    default:
      return null;
  }
}

function isUnifiedMode(): boolean {
  return typeof window !== "undefined" && isUnifiedRoute(window.location.pathname);
}

function getActionLabel(
  source: DocumentSource | null,
  disableLinkAction = false,
): string | null {
  if (!source) {
    return null;
  }

  if (disableLinkAction) {
    return null;
  }

  if (source.kind === "local" && isUnifiedMode() && isInIframe()) {
    return "Просмотр локального файла недоступен в embedded-режиме";
  }

  if (isUnifiedMode()) {
    return source.kind === "filestore"
      ? "Откроется просмотр в основном приложении"
      : "Скачать файл";
  }

  return "Открыть просмотр";
}

export function buildDocumentFileLinkTooltip({
  fileName,
  source,
  extraLines = [],
  disableLinkAction = false,
}: BuildDocumentFileLinkTooltipParams): ReactNode {
  const sourceLabel = getSourceLabel(source);
  const actionLabel = getActionLabel(source, disableLinkAction);
  const filteredExtra = extraLines.filter(Boolean);

  return (
    <div className="document-file-link-tooltip">
      <div className="document-file-link-tooltip__row">
        <span className="document-file-link-tooltip__label">Файл:</span>
        <span>{fileName}</span>
      </div>
      {sourceLabel ? (
        <div className="document-file-link-tooltip__row">
          <span className="document-file-link-tooltip__label">Источник:</span>
          <span>{sourceLabel}</span>
        </div>
      ) : null}
      {actionLabel ? (
        <div className="document-file-link-tooltip__row">
          <span className="document-file-link-tooltip__label">Действие:</span>
          <span>{actionLabel}</span>
        </div>
      ) : null}
      {filteredExtra.map((line) => (
        <div key={line} className="document-file-link-tooltip__row">
          {line}
        </div>
      ))}
    </div>
  );
}
